// Amend dialog (A only).
//
// Lightweight modal — no headless-ui dependency. Lets the admin pick a field
// from the legal allow-list, type a new value (coerced for balance_amount),
// and supply a reason.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { snapshots as snapshotsApi, ApiError } from '@/lib/api';
import type { BalanceSource, SnapshotMetadata } from '@frt/shared-types';

interface Props {
  snapshotId: number;
  currentBalance: number;
  currentSource: BalanceSource;
  currentMetadata: SnapshotMetadata;
  onClose: () => void;
}

const FIELD_OPTIONS = [
  { value: 'balance_amount', label: 'Balance amount' },
  { value: 'balance_source', label: 'Balance source (tiller / manual)' },
  { value: 'metadata_snapshot.tester_name', label: 'Metadata: Tester name' },
  { value: 'metadata_snapshot.frt_customer_code', label: 'Metadata: Customer code' },
  { value: 'metadata_snapshot.frt_customer_name', label: 'Metadata: Customer name' },
  { value: 'metadata_snapshot.account_type', label: 'Metadata: Account type' },
  { value: 'metadata_snapshot.product_type', label: 'Metadata: Product type' },
  { value: 'metadata_snapshot.frt_category', label: 'Metadata: FRT category' },
  { value: 'metadata_snapshot.tiller_status', label: 'Metadata: Tiller status' },
  { value: 'metadata_snapshot.on_point_tester_name', label: 'Metadata: On-point tester' },
];

export function AmendDialog({
  snapshotId,
  currentBalance,
  currentSource,
  currentMetadata,
  onClose,
}: Props) {
  const queryClient = useQueryClient();
  const [field, setField] = useState<string>('balance_amount');
  const [newValueRaw, setNewValueRaw] = useState<string>(String(currentBalance));
  const [reason, setReason] = useState<string>('');

  const mutation = useMutation({
    mutationFn: () =>
      snapshotsApi.amend(snapshotId, {
        field,
        new_value: coerceForField(field, newValueRaw),
        reason,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['snapshot', snapshotId] });
      await queryClient.invalidateQueries({ queryKey: ['snapshots'] });
      onClose();
    },
  });

  // When the user changes field, prefill the input with the current effective value.
  function onFieldChange(next: string) {
    setField(next);
    if (next === 'balance_amount') setNewValueRaw(String(currentBalance));
    else if (next === 'balance_source') setNewValueRaw(currentSource);
    else {
      const key = next.replace('metadata_snapshot.', '');
      setNewValueRaw(String(currentMetadata[key] ?? ''));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-lg">
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Amend snapshot</CardTitle>
          <button
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-900"
            aria-label="close"
          >
            ✕
          </button>
        </CardHeader>
        <CardBody>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!reason.trim()) return;
              mutation.mutate();
            }}
            className="space-y-3"
          >
            <label className="block text-sm">
              <span className="block text-slate-600">Field</span>
              <select
                className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={field}
                onChange={(e) => onFieldChange(e.target.value)}
              >
                {FIELD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm">
              <span className="block text-slate-600">New value</span>
              {field === 'balance_source' ? (
                <select
                  className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  value={newValueRaw}
                  onChange={(e) => setNewValueRaw(e.target.value)}
                >
                  <option value="tiller">tiller</option>
                  <option value="manual">manual</option>
                </select>
              ) : field === 'balance_amount' ? (
                <Input
                  type="number"
                  step="0.01"
                  value={newValueRaw}
                  onChange={(e) => setNewValueRaw(e.target.value)}
                />
              ) : (
                <Input value={newValueRaw} onChange={(e) => setNewValueRaw(e.target.value)} />
              )}
            </label>

            <label className="block text-sm">
              <span className="block text-slate-600">
                Reason <span className="text-red-600">*</span>
              </span>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="audit trail explanation"
                required
              />
            </label>

            {mutation.error && (
              <div className="text-sm text-red-600">
                {mutation.error instanceof ApiError
                  ? `${mutation.error.code} — ${mutation.error.message}`
                  : (mutation.error as Error).message}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!reason.trim() || mutation.isPending}>
                {mutation.isPending ? 'Submitting…' : 'Submit amendment'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function coerceForField(field: string, raw: string): unknown {
  if (field === 'balance_amount') {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      throw new Error('balance_amount must be a number');
    }
    return n;
  }
  if (field === 'balance_source') return raw;
  // Metadata fields — empty string => null, otherwise pass through.
  if (raw === '') return null;
  return raw;
}
